import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL, getSessionId, authHeaders } from '../lib/utils';

/**
 * Hook per gestire le Web Push Notifications nella PWA.
 * Registra il service worker, sottoscrive l'utente alle push e
 * salva la subscription sul backend.
 */
export function usePushNotifications() {
  const [permission, setPermission] = useState(() =>
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Converti chiave VAPID da base64url a Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
  };

  const checkSubscription = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch {}
  }, []);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const subscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return { success: false, reason: 'unsupported' };
    }
    setLoading(true);
    try {
      // 1. Chiedi permesso
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return { success: false, reason: 'denied' };
      }

      // 2. Registra service worker
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // 3. Ottieni VAPID public key dal backend
      const keyRes = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
      const { vapid_public_key } = await keyRes.json();
      if (!vapid_public_key) throw new Error('VAPID key not available');

      // 4. Sottoscrivi alle push
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_public_key),
      });

      // 5. Salva sul backend
      const sessionId = getSessionId();
      await fetch(`${BACKEND_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          subscription: subscription.toJSON(),
        }),
      });

      setIsSubscribed(true);
      setLoading(false);
      return { success: true };
    } catch (e) {
      setLoading(false);
      return { success: false, reason: e.message };
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        const sessionId = getSessionId();
        await fetch(`${BACKEND_URL}/api/push/unsubscribe?session_id=${sessionId}&endpoint=${encodeURIComponent(endpoint)}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: { ...authHeaders() },
        });
      }
      setIsSubscribed(false);
    } catch {}
  }, []);

  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  return { permission, isSubscribed, loading, isSupported, subscribe, unsubscribe };
}
